import { useTranslations } from 'next-intl'
import React, {
  useEffect,
  useState,
  // useId,
} from 'react'
import { useForm, UseFormReturn, useWatch } from 'react-hook-form'

import { PasswordInput } from '@/components/common/form/password-input'
import { VariantFormLabel } from '@/components/common/form/variant-form-label'
import DashboardLoadingIcon from '@/components/icon/dashboard-loading-icon'
import { Button } from '@/components/ui/button'
import { DialogTitle } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { AccountSecurityNameForm } from '@/components/user-preferences/account-tab/account-security-name-form'

import { useAuth } from '@/hooks/use-auth'
import { useZodResolver } from '@/hooks/use-zod-resolver'
import { ACCOUNT_SECURITY_ERROR_NAME } from '@/lib/constants'
import { sleep } from '@/lib/utils'
import { ChangePasswordSchema } from '@/schema/auth-schema'
import { authChangePasswordService } from '@/services/auth-service'

import { PreferencesSectionContainer } from '../preferences-section-container'

const AccountSecurity = () => {
  const tFormAuthCommon = useTranslations('Auth.forms.common')
  const tFormCurrent = useTranslations('Settings.forms.account_security_form')
  const tFormFields = useTranslations('Settings.forms.account_security_form.fields')
  const resolver = useZodResolver({ schema: ChangePasswordSchema, tFormFields })
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false)
  const { toast } = useToast()
  const { session } = useAuth()

  const form = useForm<typeof ChangePasswordSchema._type>({
    resolver,
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      revokeOtherSessions: false,
    },
  })

  const onSubmit = async (values: typeof ChangePasswordSchema._type) => {
    const result = await authChangePasswordService(values)

    if (!result?.error) {
      toast({
        variant: 'success',
        description: tFormCurrent('messages.submit_success'),
      })

      // delay for 2 seconds to give the user time to read the success message
      await sleep(2000)

      handleCleanup()
    }

    if (result?.error) {
      console.error('Error changing password:', result.error, result.message)

      if (result.error === ACCOUNT_SECURITY_ERROR_NAME.INVALID_CURRENT_PASSWORD) {
        toast({
          variant: 'error',
          description: tFormCurrent('messages.invalid_current_password'),
        })
      } else if (result.error === ACCOUNT_SECURITY_ERROR_NAME.INVALID_PASSWORD_EXCEPTION) {
        toast({
          variant: 'error',
          description: tFormAuthCommon('messages.invalid_password'),
        })
      } else {
        toast({
          variant: 'error',
          description: tFormAuthCommon('messages.default_error'),
        })
        throw new Error(result.error)
      }
    }
  }

  const handleCleanup = () => {
    setShowChangePasswordForm(false)
    form.reset()
  }

  return (
    <PreferencesSectionContainer>
      <div className="flex flex-col gap-1.5">
        <DialogTitle className="text-base font-semibold">{tFormCurrent('title')}</DialogTitle>
        <p className="text-sm font-normal text-muted-foreground">{tFormCurrent('description')}</p>
      </div>

      {!showChangePasswordForm ? (
        <div className="space-y-6">
          <AccountSecurityNameForm />
          <div className="space-y-2">
            <Label>{tFormCurrent('labels.email')}</Label>
            <Input type="email" className="border-none p-0" value={session?.user?.email} disabled />
          </div>

          <div className="space-y-2">
            <Label>{tFormCurrent('labels.password')}</Label>
            <div className="flex items-center justify-between gap-4">
              <Input type="password" value="••••••••••" disabled className="border-none pl-0" />
              <Button variant="outline" className="whitespace-nowrap" onClick={() => setShowChangePasswordForm(true)}>
                {tFormCurrent('actions.change_password')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem>
                    <VariantFormLabel>{tFormFields('oldPassword')}</VariantFormLabel>
                    <FormControl>
                      <PasswordInput {...field} autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <NewPasswordField tFormFields={tFormFields} form={form} />

              <FormField
                control={form.control}
                name="confirmNewPassword"
                render={({ field }) => (
                  <FormItem>
                    <VariantFormLabel>{tFormFields('confirmNewPassword')}</VariantFormLabel>
                    <FormControl>
                      <PasswordInput {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <FormField
                control={form.control}
                name="revokeOtherSessions"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Switch id={revokeOtherSessionId} checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <VariantFormLabel htmlFor={revokeOtherSessionId} className="cursor-pointer">
                      {tFormFields('revokeOtherSessions')}
                    </VariantFormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}

              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCleanup}
                  disabled={form.formState.isSubmitting}
                >
                  {tFormCurrent('actions.cancel')}
                </Button>
                <Button type="submit" variant="default" disabled={form.formState.isSubmitting}>
                  {tFormCurrent('actions.form_submit')}
                  {form.formState.isSubmitting && <DashboardLoadingIcon className="ml-2 text-base-muted" />}
                </Button>
              </div>
            </form>
          </Form>
        </>
      )}
    </PreferencesSectionContainer>
  )
}

interface NewPasswordFieldProps {
  form: UseFormReturn<typeof ChangePasswordSchema._type>
  tFormFields: (_key: string) => string
}
const NewPasswordField = ({ tFormFields, form }: NewPasswordFieldProps) => {
  const password = useWatch({ control: form.control, name: 'newPassword' })
  const isSubmitted = form.formState.isSubmitted

  // re-validate confirmNewPassword when newPassword changes and form has been submitted
  useEffect(() => {
    if (isSubmitted && password) {
      form.trigger('confirmNewPassword')
    }
  }, [password, isSubmitted, form.trigger])

  return (
    <>
      <FormField
        control={form.control}
        name="newPassword"
        render={({ field }) => (
          <FormItem>
            <VariantFormLabel>{tFormFields('newPassword')}</VariantFormLabel>
            <FormControl>
              <PasswordInput {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}

export default AccountSecurity
